/** Decodes RFC 2047 encoded-words (`=?UTF-8?Q?name.pdf?=` / `=?UTF-8?B?...?=`); unparsable words are left as-is. */
/** Bytes to text: UTF-8 via percent-decoding (no TextDecoder dependency), anything else as Latin-1. */
const bytesToText = (bytes: number[], charset: string): string =>
  /^utf-?8$/i.test(charset)
    ? decodeURIComponent(bytes.map(b => '%' + b.toString(16).padStart(2, '0')).join(''))
    : String.fromCharCode(...bytes);

const decodeEncodedWords = (value: string): string =>
  value.replace(/=\?([^?]+)\?([QqBb])\?([^?]*)\?=/g, (whole, charset: string, enc: string, text: string) => {
    try {
      if (enc.toUpperCase() === 'B') {
        return bytesToText(Array.from(atob(text), c => c.charCodeAt(0)), charset);
      }
      const bytes: number[] = [];
      const t = text.replace(/_/g, ' ');
      for (let i = 0; i < t.length; i++) {
        if (t[i] === '=' && /^[0-9A-Fa-f]{2}$/.test(t.slice(i + 1, i + 3))) {
          bytes.push(parseInt(t.slice(i + 1, i + 3), 16));
          i += 2;
        } else {
          bytes.push(t.charCodeAt(i));
        }
      }
      return bytesToText(bytes, charset);
    } catch {
      return whole;
    }
  });

/**
 * THE one place a download's filename is read from a Content-Disposition header. Prefers the RFC 5987
 * `filename*=UTF-8''...` form (percent-decoded), then a plain `filename="..."` (RFC 2047 encoded-words, which Spring's
 * ContentDisposition builder can put there, are decoded), and falls back to `fallback` when the header is missing or
 * unparsable (e.g. a cross-origin deployment that does not expose the header, or a mocked response in tests).
 */
export const parseContentDispositionFilename = (header: string | undefined | null, fallback: string): string => {
  if (!header) return fallback;
  const star = header.match(/filename\*\s*=\s*(?:UTF-8|utf-8)'[^']*'([^;]+)/);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // fall through to the plain form
    }
  }
  const quoted = header.match(/filename\s*=\s*"((?:[^"\\]|\\.)*)"/i);
  const bare = quoted ? null : header.match(/filename\s*=\s*([^";\s]+)/i);
  const raw = quoted ? quoted[1].replace(/\\(.)/g, '$1') : bare ? bare[1] : '';
  const decoded = decodeEncodedWords(raw).trim();
  return decoded || fallback;
};
